import { getInitials } from "@frontend-shared/utils";
import { getUserHandle } from "@frontend-shared/utils/user";
import { memo } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";

import type { IUserSuggestionItemProps } from "./types";

export const UserSuggestionItem = memo(
  ({
    user,
    isSelected,
    onToggle,
    unknownUserLabel,
    selectAriaLabel,
    unselectAriaLabel,
    isDisabled = false,
  }: IUserSuggestionItemProps) => {
    // NOTE: NSAI-7736 — the line under the name is the email, else the IdP
    // login name of a member the organization identifies without one.
    const handle = getUserHandle({
      email: user.email,
      username: user.username,
    });
    const initials = getInitials(user.fullName || handle);

    return (
      // NOTE: The row is the control, and it carries the invitation state for
      // assistive tech: `aria-checked` (valid on role="option", which cmdk sets)
      // announces ticked/unticked, and the label flips between Select/Unselect
      // so the action is unambiguous either way. cmdk owns `aria-selected` for
      // its keyboard highlight, which is a different thing entirely — hence
      // aria-checked rather than reusing it. `data-checked` is for tests only.
      // NSAI-7736 — the cmdk value is the uuid, not the email: a member with no
      // email has "", which cmdk never highlights, and two of them would share
      // one value.
      <CommandItem
        value={user.uuid}
        // NOTE: cmdk skips onSelect for a disabled item, so the extra guard is
        // only for the highlight-plus-Enter path the parent drives itself.
        onSelect={() => {
          if (isDisabled) return;
          onToggle(user);
        }}
        disabled={isDisabled}
        aria-checked={isSelected}
        aria-label={isSelected ? unselectAriaLabel : selectAriaLabel}
        data-checked={isSelected}
        data-testid={`share-member-option-${user.uuid}`}
        // NOTE: cmdk's own data-disabled styling already dims the row and
        // takes it out of the pointer flow; this only swaps the cursor.
        className={cn(
          "flex items-center gap-2",
          isDisabled && "cursor-not-allowed",
        )}
      >
        {/* NOTE: Purely the visual half of the row's own checked state — hidden
        from the a11y tree so it isn't announced twice, and non-interactive so it
        cannot swallow the click. */}
        <Checkbox
          checked={isSelected}
          disabled={isDisabled}
          aria-hidden="true"
          tabIndex={-1}
          className="pointer-events-none shrink-0"
        />
        <Avatar className="h-8 w-8">
          <AvatarImage src="" />
          <AvatarFallback className="bg-primary/10 text-primary text-xs">
            {initials}
          </AvatarFallback>
        </Avatar>
        {/* NOTE: NSAI-7350 — `dir="auto"` lets a Latin name or address keep its
        own direction inside an Arabic (RTL) dialog, so `truncate` cuts its END
        ("Bartholomew Montgo…") instead of its start; `rtl:text-right` keeps the
        short ones aligned with the rest of the RTL row. */}
        <div className="min-w-0 flex-1">
          <p dir="auto" className="truncate text-sm font-medium rtl:text-right">
            {user.fullName || unknownUserLabel}
          </p>
          <p
            dir="auto"
            className="truncate text-xs text-muted-foreground rtl:text-right"
          >
            {handle}
          </p>
        </div>
      </CommandItem>
    );
  },
);

UserSuggestionItem.displayName = "UserSuggestionItem";
